import { invoke } from '@shared/easyfy.decorators';

/**
 * Handles OTP digit input behavior for the 6-digit code inputs
 */
@invoke
export default class OtpDigitInputs {
  static Instance: OtpDigitInputs;

  private inputs: NodeListOf<HTMLInputElement> | null = null;
  private hiddenInput: HTMLInputElement | null = null;
  private initialized = false;

  constructor() {
    OtpDigitInputs.Instance = this;
  }

  /**
   * Public method called when panel opens - clears inputs and sets up listeners once
   */
  public reinitialize = () => {
    this.clearAllInputs();

    if (!this.initialized) {
      this.initializeInputs();
      this.initialized = true;
    }

    requestAnimationFrame(() => this.inputs?.[0]?.focus());
  };

  private initializeInputs = () => {
    this.inputs = document.querySelectorAll('.otp-digit-input');
    this.hiddenInput = document.getElementById('otpCodeValue') as HTMLInputElement;

    if (!this.inputs || this.inputs.length === 0) return;

    this.inputs.forEach((input, index) => {
      input.addEventListener('input', (e) => this.handleInput(e, index));
      input.addEventListener('keydown', (e) => this.handleKeyDown(e, index));
      input.addEventListener('paste', (e) => this.handlePaste(e, index));
    });
  };

  private handleInput = (e: Event, index: number) => {
    const input = e.target as HTMLInputElement;
    const value = input.value;

    this.clearError();

    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      input.value = '';
      return;
    }

    // If value entered, move to next input
    if (value.length === 1 && index < 5) {
      const nextInput = this.inputs?.[index + 1] as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }

    this.updateHiddenInput();
  };

  private handleKeyDown = (e: KeyboardEvent, index: number) => {
    const input = e.target as HTMLInputElement;

    // Handle backspace - improved behavior
    if (e.key === 'Backspace') {
      if (input.value) {
        // If current input has value, clear it
        input.value = '';
        this.updateHiddenInput();
      } else if (index > 0) {
        // If current input is empty, go to previous and clear it
        const prevInput = this.inputs?.[index - 1] as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
          prevInput.value = '';
          this.updateHiddenInput();
        }
      }
      e.preventDefault();
    }

    // Handle delete key
    if (e.key === 'Delete') {
      input.value = '';
      this.updateHiddenInput();
      e.preventDefault();
    }

    // Handle left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = this.inputs?.[index - 1] as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Handle right arrow
    if (e.key === 'ArrowRight' && index < 5) {
      const nextInput = this.inputs?.[index + 1] as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  private handlePaste = (e: ClipboardEvent, index: number) => {
    e.preventDefault();

    const pasteData = e.clipboardData?.getData('text');
    if (!pasteData) return;

    // Extract only digits
    const digits = pasteData.replace(/\D/g, '').slice(0, 6);

    if (!this.inputs) return;

    // Fill inputs with pasted digits
    digits.split('').forEach((digit, i) => {
      if (this.inputs && index + i < 6) {
        const targetInput = this.inputs[index + i] as HTMLInputElement;
        targetInput.value = digit;
      }
    });

    // Focus on next empty input or last input
    const nextEmptyIndex = Math.min(index + digits.length, 5);
    const nextInput = this.inputs[nextEmptyIndex] as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }

    this.updateHiddenInput();
  };

  private updateHiddenInput = () => {
    if (!this.inputs || !this.hiddenInput) return;

    const code = Array.from(this.inputs)
      .map((input) => input.value)
      .join('');

    this.hiddenInput.value = code;

    // Auto-submit if all 6 digits are filled
    if (code.length === 6) {
      this.submitForm();
    }
  };

  private submitForm = () => {
    const form = document.querySelector('[data-container="otp-validate-form"]') as HTMLFormElement;
    if (form) {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  public setErrorState = () => {
    this.clearAllInputs();
    document.querySelector('.otp-code-inputs')?.classList.add('error');
    requestAnimationFrame(() => this.inputs?.[0]?.focus());
  };

  private clearError = () => {
    document.querySelector('.otp-code-inputs')?.classList.remove('error');
    const validationSummary = document.querySelector('[data-container="otp-validate-form"] [data-container="validation-summary"]');
    if (validationSummary) {
      validationSummary.textContent = '';
    }
  };

  private clearAllInputs = () => {
    if (!this.inputs) return;

    this.inputs.forEach((input) => {
      input.value = '';
    });

    if (this.hiddenInput) {
      this.hiddenInput.value = '';
    }
  };
}
